import * as React from 'react';

type PermissionsState = {
  allowed: Set<string>;
  can: (key: string) => boolean;
  canAny: (keys: readonly string[]) => boolean;
  canAll: (keys: readonly string[]) => boolean;
};

const PermissionsContext = React.createContext<PermissionsState | null>(null);

export function PermissionsProvider({
  allowedKeys,
  children,
}: {
  allowedKeys: Set<string>;
  children: React.ReactNode;
}) {
  // keep stable reference for functions (allowedKeys itself can be memoized by caller)
  const value = React.useMemo<PermissionsState>(
    () => ({
      allowed: allowedKeys,
      can: (key) => allowedKeys.has(key),
      canAny: (keys) => keys.some((k) => allowedKeys.has(k)),
      canAll: (keys) => keys.every((k) => allowedKeys.has(k)),
    }),
    [allowedKeys],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  const ctx = React.useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used inside PermissionsProvider');
  return ctx;
}
